import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search, X } from "lucide-react";

/**
 * Autocomplete combobox for selecting a school.
 * Accepts a list of {id,name,nickname} objects and calls `onChange(id)`.
 */
export default function SchoolCombobox({ schools = [], value, onChange, disabled = false, placeholder = "Digite para buscar..." }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef(null);

  const selected = useMemo(
    () => schools.find((s) => s.id === value) || null,
    [schools, value]
  );

  useEffect(() => {
    const onClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const normalize = (s) =>
    String(s || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

  const filtered = useMemo(() => {
    const q = normalize(query);
    if (!q) return schools.slice(0, 50);
    return schools
      .filter((s) =>
        normalize(s.name).includes(q) || normalize(s.nickname).includes(q)
      )
      .slice(0, 50);
  }, [schools, query]);

  return (
    <div ref={containerRef} className="relative" data-testid="school-combobox">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        data-testid="school-combobox-trigger"
        className={`w-full h-10 flex items-center justify-between gap-2 px-3 rounded-md border border-slate-200 bg-white text-sm text-left ${
          disabled ? "opacity-60 cursor-not-allowed" : "hover:border-[#7c3aed]"
        }`}
      >
        <span className={selected ? "text-slate-900 truncate" : "text-slate-400"}>
          {selected ? (selected.name + (selected.nickname ? ` — ${selected.nickname}` : "")) : placeholder}
        </span>
        <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
      </button>

      {open && !disabled && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg">
          <div className="relative p-2 border-b border-slate-100">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Digite o nome da escola..."
              data-testid="school-combobox-search"
              className="w-full h-9 pl-8 pr-8 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#7c3aed]"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <ul className="max-h-72 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-xs text-slate-500 text-center">Nenhuma escola encontrada</li>
            ) : (
              filtered.map((s) => {
                const active = s.id === value;
                return (
                  <li key={s.id}>
                    <button
                      type="button"
                      onClick={() => {
                        onChange(s.id);
                        setOpen(false);
                        setQuery("");
                      }}
                      data-testid={`school-combobox-item-${s.id}`}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 ${
                        active ? "bg-[#7c3aed]/10 text-[#7c3aed] font-semibold" : "text-slate-800"
                      }`}
                    >
                      {s.name}
                      {s.nickname && <span className="text-slate-400 ml-1">— {s.nickname}</span>}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
          {filtered.length >= 50 && (
            <p className="px-3 py-1 text-[10px] text-slate-400 border-t border-slate-100 text-center">
              Refine a busca — mostrando primeiras 50
            </p>
          )}
        </div>
      )}
    </div>
  );
}
